import {useFocusManagement} from '@aboutyou/a11y';
import {useTranslation} from '@aboutyou/i18n';
import {Fade} from '@aboutyou/transitions';
import {
  BlurredOverlay,
  Button,
  COLORS,
  GenericIconName,
  Icon,
  RichText,
  UnstyledButton,
  Z_INDEXES,
  useKeyPress,
  usePortalElement,
} from '@aboutyou/ui-components';
import {useOS} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React from 'react';
import {createPortal} from 'react-dom';

export const PopupOverlay: React.FC<{
  isOpen: boolean;
  onClick: () => void;
  onClose: () => void;
}> = ({onClick, isOpen, onClose}) => {
  const portal = usePortalElement('login-backdrop');
  const {t} = useTranslation();

  const {focusManagementRefCallback, isTopMostPopover} = useFocusManagement({
    isOpen,
    priority: 1,
  });

  useKeyPress({
    key: 'Escape',
    onKeyDown: onClose,
    disabled: isTopMostPopover === false,
  });

  const isIOS = useOS() === 'ios';

  /**
   * In iOS there is a limitation to refocus the opened popup window.
   * There we do not render the overlay to not confuse the user.
   *
   * Instead we allow the user to click his preferred SSO button again to and reopen the popup.
   */
  if (!portal || isIOS) {
    return null;
  }

  return createPortal(
    <Fade mounted={isOpen} duration={500}>
      <Backdrop onClick={onClick}>
        <ContentWrapper ref={focusManagementRefCallback()}>
          <StyledCloseButton aria-label={t('Close')} onClick={onClose}>
            <Icon asset={GenericIconName.Close} size={'M'} fill={'white'} />
          </StyledCloseButton>

          <StyledText size="px18" color="White">
            {t(
              "Window not appearing? <br /> We'll help you open it to sign in.",
            )}
          </StyledText>

          <StyledButton variant="secondary" size="fit-width">
            {t('Continue')}
          </StyledButton>
        </ContentWrapper>
      </Backdrop>
    </Fade>,
    portal,
  );
};

const Backdrop = styled(BlurredOverlay)`
  z-index: ${Z_INDEXES.TO_THE_TOP};
  background-image: radial-gradient(rgb(0 0 0 / 66%), rgb(0 0 0 / 36%));
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const StyledText = styled(RichText)`
  text-align: center;
  line-height: 1.5;
`;

const StyledButton = styled(Button)`
  margin-top: 16px;
`;

const StyledCloseButton = styled(UnstyledButton)`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  border: none;
  padding: 24px;
  display: flex;
  box-sizing: content-box;

  &:focus-visible {
    outline: 2px solid ${COLORS.White};
  }
`;
